import { z } from 'zod';
import { asId, type AreaId, type PersonId, type SubAreaId } from '@shared/brand';
import type { AuditEntry } from '@domain/entities/AuditEntry';
import type { Balance } from '@domain/entities/Balance';
import type { Holiday } from '@domain/entities/Holiday';
import type { Incapacity } from '@domain/entities/Incapacity';
import type { Area, Company, SubArea } from '@domain/entities/Organization';
import { SUB_AREA_FALLBACK_COLOR } from '@domain/entities/Organization';
import type { PermissionRequest } from '@domain/entities/PermissionRequest';
import { type HourWindow, isHourString } from '@domain/entities/PermissionRequest';
import type { EmergencyContact, Person, PersonLeaderLink, PersonDocumentRef } from '@domain/entities/Person';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@domain/entities/Person';
import type {
  ReplacementDecision,
  RequestApproval,
  RequestWorkItem,
  VacationRequest,
} from '@domain/entities/VacationRequest';
import { APPROVAL_DECISIONS, REPLACEMENT_DECISIONS } from '@domain/entities/VacationRequest';
import {
  AUDIT_KINDS,
  HOLIDAY_TYPES,
  INCAPACITY_STATUSES,
  PERMISSION_REQUEST_STATUSES,
  PERSON_STATUSES,
  REQUEST_STATUSES,
  ROLES,
  isParentRole,
  isShirtSize,
  isWorkMode,
} from '@domain/vocabulary';
import {
  arrayOr,
  auditableShape,
  booleanOr,
  calendarDateSchema,
  dateRangeFrom,
  enumOr,
  integerOr,
  nullableText,
  nullableTimestamp,
  safeDecode,
  textOr,
  trimmedTextOr,
  vocabularyOr,
} from './base';

/**
 * Firestore document → domain entity.
 *
 * Each decoder returns `null` when a field the entity genuinely cannot exist
 * without is unusable — a request with no dates, a person with no email. Anything
 * softer falls back, so a document merely written by an older or newer build still
 * decodes. `decodeMany` turns those nulls into skipped rows rather than a failed
 * page.
 *
 * The denormalised display fields (`personName`, `subAreaName`) are decoded with
 * an em-dash fallback rather than being resolved: a row whose copy is stale should
 * render with a placeholder, not trigger a lookup that turns one list read into
 * five hundred.
 */

const MISSING = '—';

const id = <B extends string>(raw: string) => asId<B>(raw);

const nullableId = <B extends string>(value: unknown) =>
  typeof value === 'string' && value.trim().length > 0 ? asId<B>(value.trim()) : null;

// ---------------------------------------------------------------------------
// Organisation
// ---------------------------------------------------------------------------

const companySchema = z.object({
  ...auditableShape,
  name: textOr('Empresa'),
  allowedDomains: arrayOr(z.string().min(1)),
  hrEmail: textOr(''),
  country: textOr(''),
  logoPath: nullableText(),
});

export const decodeCompany = (docId: string, data: unknown): Company | null => {
  const parsed = safeDecode(companySchema, data);
  if (parsed === null) return null;
  return {
    id: id<'CompanyId'>(docId),
    name: parsed.name,
    // A company with no allowed domain would let anybody in, so the empty case
    // falls back to nothing allowed rather than to everything.
    allowedDomains: parsed.allowedDomains.map((d) => d.replace(/^@/, '').toLowerCase()),
    hrEmail: parsed.hrEmail,
    country: parsed.country,
    logoPath: parsed.logoPath,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

const areaSchema = z.object({
  ...auditableShape,
  companyId: textOr(''),
  name: textOr(MISSING),
  color: textOr(SUB_AREA_FALLBACK_COLOR),
  active: booleanOr(true),
  adminEmail: textOr(''),
});

export const decodeArea = (docId: string, data: unknown): Area | null => {
  const parsed = safeDecode(areaSchema, data);
  if (parsed === null) return null;
  return {
    id: id<'AreaId'>(docId),
    companyId: id<'CompanyId'>(parsed.companyId),
    name: parsed.name,
    color: parsed.color,
    active: parsed.active,
    adminEmail: parsed.adminEmail,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

const subAreaSchema = z.object({
  ...auditableShape,
  areaId: z.string().min(1),
  name: textOr(MISSING),
  // A missing cap must not read as zero, which would flag every request in the
  // sub area as a breach. Two matches the handoff's most common setting.
  maxConcurrentOut: integerOr(2),
  leadPersonId: z.unknown(),
  color: textOr(''),
});

export const decodeSubArea = (docId: string, data: unknown): SubArea | null => {
  const parsed = safeDecode(subAreaSchema, data);
  if (parsed === null) return null;
  return {
    id: id<'SubAreaId'>(docId),
    areaId: id<'AreaId'>(parsed.areaId),
    name: parsed.name,
    maxConcurrentOut: Math.max(0, parsed.maxConcurrentOut),
    leadPersonId: nullableId<'PersonId'>(parsed.leadPersonId),
    color: parsed.color,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

const notificationPrefsSchema = z
  .unknown()
  .transform((value) => {
    if (typeof value !== 'object' || value === null) return DEFAULT_NOTIFICATION_PREFERENCES;
    const raw = value as Record<string, unknown>;
    return {
      email: typeof raw['email'] === 'boolean' ? raw['email'] : DEFAULT_NOTIFICATION_PREFERENCES.email,
      slack: typeof raw['slack'] === 'boolean' ? raw['slack'] : DEFAULT_NOTIFICATION_PREFERENCES.slack,
      reminder:
        typeof raw['reminder'] === 'boolean'
          ? raw['reminder']
          : DEFAULT_NOTIFICATION_PREFERENCES.reminder,
    };
  });

const personSchema = z.object({
  ...auditableShape,
  uid: nullableText(),
  /*
   * Vacío es válido: hay personas **sin correo a propósito** —limpieza,
   * mantenimiento, cafetería— cuyo líder les registra las vacaciones desde el
   * portal. Esto decía «sin correo la ficha no sirve» y la descartaba en silencio:
   * el 29 sep 2026 eran 11 fichas activas que no salían en ninguna lista —ni en
   * Personas, ni en el organigrama, ni para registrarles vacaciones— aunque
   * existían y tenían líder. Quien no tiene correo tampoco puede iniciar sesión,
   * y eso ya lo garantiza que no haya cuenta que casar con una dirección vacía.
   */
  email: trimmedTextOr(''),
  displayName: textOr(MISSING),
  /*
   * Vacía es válida, y no es un descuido: es una **cuenta por validar**.
   *
   * Quien entra con Google sin que RR.HH. le haya creado ficha nace `pending` y
   * sin área —`beforeUserCreatedGate`—, y esa ficha es justo la que «Cuentas por
   * validar» le enseña a quien ve todas las áreas. Con `min(1)` el decodificador
   * la descartaba en silencio: la consulta la traía y la pantalla decía «Nada
   * pendiente». Lo enseñó JC el 14 oct 2026: una persona con ficha activa entró
   * con otro correo, se quedó en «Tu cuenta está en camino» y nadie podía verla
   * para juntarla. Una ficha activa siempre tiene área; eso lo garantizan la
   * validación y las reglas, no este decodificador.
   */
  areaId: z.string(),
  subAreaId: z.unknown(),
  // An unrecognised role falls back to the least privileged one. Failing open
  // here would be the worst possible direction.
  /* Listas de texto y no de `Permission`: un permiso que este build no conozca se
     descarta al calcular el efectivo, no al leer la ficha — así una ficha
     guardada por una versión más nueva sigue siendo legible por una más vieja. */
  /* `null` y no un valor por omisión: quien entró antes de que el campo existiera
     no tiene modalidad, y adivinarla sería inventar un dato de recursos humanos. */
  workMode: z
    .unknown()
    .transform((value) => (isWorkMode(value) ? value : null)),
  computerEquipment: textOr(''),
  /* Sin forma cerrada a propósito: los tipos de documento los decide
     `PERSON_DOCUMENTS`, y una ficha guardada por una versión con un papel más
     tiene que seguir siendo legible por una versión que no lo conoce. */
  documents: z.unknown().transform((value) => (typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {})),
  extraPermissions: arrayOr(textOr('')),
  revokedPermissions: arrayOr(textOr('')),
  role: enumOr(ROLES, 'empleado'),
  // An unrecognised status falls back to `pending`, which grants nothing.
  status: enumOr(PERSON_STATUSES, 'pending'),
  hireDate: calendarDateSchema,
  /*
   * Los tres del padrón de RR.HH. de septiembre de 2026, y los tres ausentes en
   * todo documento anterior.
   *
   * `birthDate` se lee con el mismo decodificador que `hireDate` —una cadena ISO
   * o `null`—, así que una fecha corrupta no tumba la ficha: la deja sin
   * cumpleaños, que es lo que ya pasa con las 8 filas que no la traen.
   *
   * Los otros dos se leen contra su catálogo y caen a `null` si no lo reconocen.
   * Es la misma regla que `workMode`: una ficha guardada por una versión que
   * conozca una talla más tiene que seguir siendo legible por ésta.
   */
  birthDate: calendarDateSchema,
  shirtSize: z.unknown().transform((value) => (isShirtSize(value) ? value : null)),
  parentRole: z.unknown().transform((value) => (isParentRole(value) ? value : null)),
  /*
   * Ausente en casi todas las fichas y por eso opcional con respaldo: un campo que
   * sólo escribe la corrección administrativa no puede tumbar la lectura de las 421
   * que no lo tienen. `catch` lo deja en `null`, que significa «según antigüedad».
   */
  entitledDaysOverride: z.number().int().positive().nullable().catch(null),
  photoURL: nullableText(),
  notificationPrefs: notificationPrefsSchema,
  /*
   * Ausente en todo documento anterior al alta estructurada, que son todos los
   * de producción hoy. `optional` y no `nullable` a secas: un cliente nuevo tiene
   * que poder leer un documento viejo que no trae la clave.
   */
  /* Ausente en todo documento anterior al catálogo de puestos. */
  positionId: nullableText().catch(null),
  /*
   * La baja. `nullish` porque una persona activa simplemente no la trae, y un
   * cliente viejo tiene que poder leer el documento de alguien dado de baja por
   * uno nuevo sin caerse.
   */
  termination: z
    .object({
      date: calendarDateSchema,
      reason: z.string(),
      byPersonId: z.string(),
    })
    .nullish()
    .catch(null),
  nameParts: z
    .object({
      primerNombre: z.string(),
      segundoNombre: nullableText(),
      primerApellido: z.string(),
      segundoApellido: nullableText(),
    })
    .nullish()
    .catch(null),
});

/** Cada entrada, tolerante: lo que no se entienda se descarta sin tirar la ficha. */
const decodeDocuments = (raw: Record<string, unknown>): Record<string, PersonDocumentRef> => {
  const salida: Record<string, PersonDocumentRef> = {};
  for (const [kind, value] of Object.entries(raw)) {
    if (value === null || typeof value !== 'object') continue;
    const entry = value as Record<string, unknown>;
    const storagePath = typeof entry['storagePath'] === 'string' ? entry['storagePath'] : '';
    if (storagePath.length === 0) continue;
    const at = entry['uploadedAt'];
    salida[kind] = {
      storagePath,
      uploadedAt:
        at !== null && typeof at === 'object' && 'toDate' in at
          ? (at as { toDate: () => Date }).toDate()
          : null,
      uploadedByPersonId:
        typeof entry['uploadedByPersonId'] === 'string' ? entry['uploadedByPersonId'] : null,
    };
  }
  return salida;
};

export const decodePerson = (docId: string, data: unknown): Person | null => {
  const parsed = safeDecode(personSchema, data);
  if (parsed === null) return null;
  return {
    id: id<'PersonId'>(docId),
    uid: parsed.uid,
    nameParts: parsed.nameParts ?? null,
    positionId: parsed.positionId,
    termination:
      parsed.termination === null ||
      parsed.termination === undefined ||
      parsed.termination.date === null
        ? null
        : {
            date: parsed.termination.date,
            reason: parsed.termination.reason,
            byPersonId: id<'PersonId'>(parsed.termination.byPersonId),
          },
    email: parsed.email.toLowerCase(),
    displayName: parsed.displayName,
    areaId: id<'AreaId'>(parsed.areaId),
    subAreaId: nullableId<'SubAreaId'>(parsed.subAreaId),
    role: parsed.role,
    workMode: parsed.workMode,
    computerEquipment: parsed.computerEquipment,
    documents: decodeDocuments(parsed.documents),
    extraPermissions: parsed.extraPermissions,
    revokedPermissions: parsed.revokedPermissions,
    status: parsed.status,
    hireDate: parsed.hireDate,
    birthDate: parsed.birthDate,
    shirtSize: parsed.shirtSize,
    parentRole: parsed.parentRole,
    entitledDaysOverride: parsed.entitledDaysOverride,
    photoURL: parsed.photoURL,
    notificationPrefs: parsed.notificationPrefs,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

const personLeaderSchema = z.object({
  ...auditableShape,
  personId: z.string().min(1),
  leaderPersonId: z.string().min(1),
  areaId: textOr(''),
  projectLabel: textOr(''),
});

export const decodePersonLeaderLink = (
  docId: string,
  data: unknown,
): PersonLeaderLink | null => {
  const parsed = safeDecode(personLeaderSchema, data);
  if (parsed === null) return null;
  return {
    id: docId,
    personId: id<'PersonId'>(parsed.personId),
    leaderPersonId: id<'PersonId'>(parsed.leaderPersonId),
    areaId: id<'AreaId'>(parsed.areaId),
    // Blank for links written before the field existed; the UI falls back to a
    // neutral label rather than showing an empty parenthesis.
    projectLabel: parsed.projectLabel,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

const emergencyContactSchema = z.object({
  ...auditableShape,
  personId: z.string().min(1),
  areaId: textOr(''),
  name: trimmedTextOr(MISSING),
  relation: trimmedTextOr(''),
  phone: trimmedTextOr(''),
  preferenceOrder: integerOr(0),
});

export const decodeEmergencyContact = (
  docId: string,
  data: unknown,
): EmergencyContact | null => {
  const parsed = safeDecode(emergencyContactSchema, data);
  if (parsed === null) return null;
  return {
    id: id<'EmergencyContactId'>(docId),
    personId: id<'PersonId'>(parsed.personId),
    areaId: id<'AreaId'>(parsed.areaId),
    name: parsed.name,
    relation: parsed.relation,
    phone: parsed.phone,
    preferenceOrder: Math.max(0, parsed.preferenceOrder),
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

// ---------------------------------------------------------------------------
// Vacation requests
// ---------------------------------------------------------------------------

const vacationRequestSchema = z.object({
  ...auditableShape,
  folio: textOr(MISSING),
  personId: z.string().min(1),
  personName: textOr(MISSING),
  areaId: z.string().min(1),
  subAreaId: z.unknown(),
  subAreaName: textOr(MISSING),
  startDate: z.unknown(),
  endDate: z.unknown(),
  businessDays: integerOr(0),
  replacementPersonId: z.unknown(),
  replacementName: textOr(''),
  replacementDecision: nullableText(),
  replacementReason: nullableText(),
  replacementDecidedAt: nullableTimestamp(),
  replacementWaivedReason: nullableText(),
  status: vocabularyOr(REQUEST_STATUSES),
  pdfPath: nullableText(),
  notes: textOr(''),
  requiredSignatures: integerOr(1),
  resolutionReason: nullableText(),
  assignedByName: nullableText(),
  /* Ausente en las 33 solicitudes que nadie registró por cuenta ajena, y en
     toda la colección antes del 21 de agosto. `nullableText` lo deja en `null`,
     que es exactamente «la pidió su dueña». */
  assignedByPersonId: nullableText(),
  decidedAt: nullableTimestamp(),
  rescheduledFromFolio: nullableText(),
  submittedAt: nullableTimestamp(),
});

export const decodeVacationRequest = (docId: string, data: unknown): VacationRequest | null => {
  const parsed = safeDecode(vacationRequestSchema, data);
  if (parsed === null) return null;

  // A request without a valid range cannot be rendered on a calendar, counted, or
  // reasoned about. Skipping it is better than inventing dates.
  const range = dateRangeFrom(parsed.startDate, parsed.endDate);
  if (range === null) return null;

  return {
    id: id<'RequestId'>(docId),
    folio: parsed.folio,
    personId: id<'PersonId'>(parsed.personId),
    personName: parsed.personName,
    areaId: id<'AreaId'>(parsed.areaId),
    subAreaId: nullableId<'SubAreaId'>(parsed.subAreaId),
    subAreaName: parsed.subAreaName,
    range,
    businessDays: Math.max(0, parsed.businessDays),
    replacementPersonId: nullableId<'PersonId'>(parsed.replacementPersonId),
    replacementName: parsed.replacementName,
    /*
     * Absent on every request written before the backup had to confirm, and
     * `pending` would be a lie about those: nobody was ever asked. Null reads as
     * «no hay nada que esperar», which is what the approve button needs.
     */
    replacementDecision: REPLACEMENT_DECISIONS.includes(
      parsed.replacementDecision as ReplacementDecision,
    )
      ? (parsed.replacementDecision as ReplacementDecision)
      : null,
    replacementReason: parsed.replacementReason,
    replacementDecidedAt: parsed.replacementDecidedAt,
    replacementWaivedReason: parsed.replacementWaivedReason,
    status: parsed.status,
    pdfPath: parsed.pdfPath,
    notes: parsed.notes,
    // Clamped to the two values the approval chain supports. A document claiming
    // three signatures would otherwise make the request unapprovable forever.
    requiredSignatures: parsed.requiredSignatures >= 2 ? 2 : 1,
    resolutionReason: parsed.resolutionReason,
    assignedByName: parsed.assignedByName,
    assignedByPersonId: nullableId<'PersonId'>(parsed.assignedByPersonId),
    decidedAt: parsed.decidedAt,
    rescheduledFromFolio: parsed.rescheduledFromFolio,
    submittedAt: parsed.submittedAt,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

const requestApprovalSchema = z.object({
  ...auditableShape,
  requestId: z.string().min(1),
  approverPersonId: z.string().min(1),
  approverName: textOr(MISSING),
  order: integerOr(1),
  decision: enumOr(APPROVAL_DECISIONS, 'pending'),
  decidedAt: nullableTimestamp(),
  reason: nullableText(),
});

export const decodeRequestApproval = (docId: string, data: unknown): RequestApproval | null => {
  const parsed = safeDecode(requestApprovalSchema, data);
  if (parsed === null) return null;
  return {
    id: id<'ApprovalId'>(docId),
    requestId: id<'RequestId'>(parsed.requestId),
    approverPersonId: id<'PersonId'>(parsed.approverPersonId),
    approverName: parsed.approverName,
    order: parsed.order >= 2 ? 2 : 1,
    decision: parsed.decision,
    decidedAt: parsed.decidedAt,
    reason: parsed.reason,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

const workItemSchema = z.object({
  ...auditableShape,
  requestId: z.string().min(1),
  ticket: textOr(''),
  text: textOr(''),
});

export const decodeRequestWorkItem = (docId: string, data: unknown): RequestWorkItem | null => {
  const parsed = safeDecode(workItemSchema, data);
  if (parsed === null) return null;
  return {
    id: id<'WorkItemId'>(docId),
    requestId: id<'RequestId'>(parsed.requestId),
    ticket: parsed.ticket,
    text: parsed.text,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

// ---------------------------------------------------------------------------
// Permissions and incapacities
// ---------------------------------------------------------------------------

/**
 * La ventana de horas de un permiso, en cualquiera de sus tres formas.
 *
 * `earlyHours` primero, porque es la única que no lleva reloj: una salida
 * anticipada pedida por duración no tiene hora de salida que validar.
 *
 * Después, `to` ausente o nulo con `from` puesto es la forma antigua —sin
 * regreso, con hora— y se conserva como nulo en vez de descartarse: descartarla
 * dejaría `hours: null` y la fila se leería como un permiso de día completo, que
 * es justo lo contrario de lo que se pidió. Sólo un `to` presente y con formato
 * inválido invalida la ventana entera.
 */
const hourWindowFrom = (value: unknown): HourWindow | null => {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;

  const early = raw['earlyHours'];
  if (typeof early === 'number' && Number.isFinite(early) && early > 0) {
    return { from: null, to: null, earlyHours: Math.trunc(early) };
  }

  const from = typeof raw['from'] === 'string' ? raw['from'] : '';
  if (!isHourString(from)) return null;
  const rawTo = raw['to'];
  if (rawTo === null || rawTo === undefined) return { from, to: null, earlyHours: null };
  const to = typeof rawTo === 'string' ? rawTo : '';
  if (!isHourString(to)) return null;
  return { from, to, earlyHours: null };
};

const permissionRequestSchema = z.object({
  ...auditableShape,
  folio: textOr(MISSING),
  personId: z.string().min(1),
  personName: textOr(MISSING),
  areaId: z.string().min(1),
  subAreaId: z.unknown(),
  subAreaName: textOr(MISSING),
  kind: textOr(MISSING),
  startDate: z.unknown(),
  endDate: z.unknown(),
  hours: z.unknown(),
  withPay: booleanOr(true),
  reason: textOr(''),
  status: vocabularyOr(PERMISSION_REQUEST_STATUSES),
  decidedByPersonId: z.unknown(),
  decidedByName: textOr(''),
  decidedAt: nullableTimestamp(),
  resolutionReason: nullableText(),
});

export const decodePermissionRequest = (
  docId: string,
  data: unknown,
): PermissionRequest | null => {
  const parsed = safeDecode(permissionRequestSchema, data);
  if (parsed === null) return null;

  const range = dateRangeFrom(parsed.startDate, parsed.endDate);
  if (range === null) return null;

  return {
    id: id<'PermissionRequestId'>(docId),
    folio: parsed.folio,
    personId: id<'PersonId'>(parsed.personId),
    personName: parsed.personName,
    areaId: id<'AreaId'>(parsed.areaId),
    subAreaId: nullableId<'SubAreaId'>(parsed.subAreaId),
    subAreaName: parsed.subAreaName,
    kind: parsed.kind,
    range,
    hours: hourWindowFrom(parsed.hours),
    withPay: parsed.withPay,
    reason: parsed.reason,
    status: parsed.status,
    decidedByPersonId: nullableId<'PersonId'>(parsed.decidedByPersonId),
    decidedByName: parsed.decidedByName,
    decidedAt: parsed.decidedAt,
    resolutionReason: parsed.resolutionReason,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

const incapacitySchema = z.object({
  ...auditableShape,
  folio: textOr(MISSING),
  personId: z.string().min(1),
  personName: textOr(MISSING),
  areaId: z.string().min(1),
  subAreaId: z.unknown(),
  subAreaName: textOr(MISSING),
  kind: textOr(MISSING),
  startDate: z.unknown(),
  endDate: z.unknown(),
  naturalDays: integerOr(0),
  imssFolio: textOr(''),
  umf: textOr(''),
  subsidy: textOr(''),
  coverage: textOr(''),
  certificatePath: nullableText(),
  status: vocabularyOr(INCAPACITY_STATUSES),
  notes: textOr(''),
  validatedByPersonId: z.unknown(),
  validatedAt: nullableTimestamp(),
  observation: nullableText(),
});

export const decodeIncapacity = (docId: string, data: unknown): Incapacity | null => {
  const parsed = safeDecode(incapacitySchema, data);
  if (parsed === null) return null;

  const range = dateRangeFrom(parsed.startDate, parsed.endDate);
  if (range === null) return null;

  return {
    id: id<'IncapacityId'>(docId),
    folio: parsed.folio,
    personId: id<'PersonId'>(parsed.personId),
    personName: parsed.personName,
    areaId: id<'AreaId'>(parsed.areaId),
    subAreaId: nullableId<'SubAreaId'>(parsed.subAreaId),
    subAreaName: parsed.subAreaName,
    kind: parsed.kind,
    range,
    // Medical leave is counted in natural days, not business days: the subsidy is
    // paid per calendar day. A stored zero falls back to the range's own length
    // rather than showing an absence that costs nothing.
    naturalDays: parsed.naturalDays > 0 ? parsed.naturalDays : range.naturalDays,
    imssFolio: parsed.imssFolio,
    umf: parsed.umf,
    subsidy: parsed.subsidy,
    coverage: parsed.coverage,
    certificatePath: parsed.certificatePath,
    status: parsed.status,
    notes: parsed.notes,
    validatedByPersonId: nullableId<'PersonId'>(parsed.validatedByPersonId),
    validatedAt: parsed.validatedAt,
    observation: parsed.observation,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

// ---------------------------------------------------------------------------
// Holidays, balances, audit log
// ---------------------------------------------------------------------------

const holidaySchema = z.object({
  ...auditableShape,
  companyId: textOr(''),
  year: integerOr(0),
  date: calendarDateSchema,
  name: textOr(MISSING),
  type: vocabularyOr(HOLIDAY_TYPES),
  enabled: booleanOr(true),
});

export const decodeHoliday = (docId: string, data: unknown): Holiday | null => {
  const parsed = safeDecode(holidaySchema, data);
  if (parsed === null || parsed.date === null) return null;
  return {
    id: id<'HolidayId'>(docId),
    companyId: id<'CompanyId'>(parsed.companyId),
    // `year` is stored for querying but derived here when absent, so a
    // hand-created holiday still lands in the right calendar.
    year: parsed.year > 0 ? parsed.year : parsed.date.year,
    date: parsed.date,
    name: parsed.name,
    type: parsed.type,
    enabled: parsed.enabled,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

const balanceSchema = z.object({
  ...auditableShape,
  personId: z.string().min(1),
  personName: textOr(MISSING),
  areaId: textOr(''),
  year: integerOr(0),
  entitledDays: integerOr(0),
  // Absent on every document written before the migration existed, so it decodes
  // to zero — which is exactly right: nothing was consumed outside the portal.
  openingUsedDays: integerOr(0),
  // Sólo lo escribe `ensureBalance` al crear un periodo desde el 21 sep 2026; lo
  // importado no lo trae, y ahí cero es exacto: no había deuda que arrastrar.
  carriedDebtDays: integerOr(0),
  takenDays: integerOr(0),
  scheduledDays: integerOr(0),
  pendingDays: integerOr(0),
  expiringDays: integerOr(0),
  expiresOn: calendarDateSchema,
  remainingDays: integerOr(0),
});

export const decodeBalance = (docId: string, data: unknown): Balance | null => {
  const parsed = safeDecode(balanceSchema, data);
  if (parsed === null) return null;
  return {
    id: id<'BalanceId'>(docId),
    personId: id<'PersonId'>(parsed.personId),
    personName: parsed.personName,
    areaId: id<'AreaId'>(parsed.areaId),
    year: parsed.year,
    entitledDays: Math.max(0, parsed.entitledDays),
    openingUsedDays: Math.max(0, parsed.openingUsedDays),
    carriedDebtDays: Math.min(Math.max(0, parsed.carriedDebtDays), Math.max(0, parsed.openingUsedDays)),
    takenDays: Math.max(0, parsed.takenDays),
    scheduledDays: Math.max(0, parsed.scheduledDays),
    pendingDays: Math.max(0, parsed.pendingDays),
    expiringDays: Math.max(0, parsed.expiringDays),
    expiresOn: parsed.expiresOn,
    remainingDays: Math.max(0, parsed.remainingDays),
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

const auditEntrySchema = z.object({
  ...auditableShape,
  areaId: textOr(''),
  areaName: textOr(MISSING),
  kind: vocabularyOr(AUDIT_KINDS),
  actorPersonId: z.unknown(),
  actorName: textOr(MISSING),
  action: textOr(''),
  detail: textOr(''),
  documentPath: nullableText(),
  targetPath: nullableText(),
});

export const decodeAuditEntry = (docId: string, data: unknown): AuditEntry | null => {
  const parsed = safeDecode(auditEntrySchema, data);
  if (parsed === null) return null;
  return {
    id: id<'AuditEntryId'>(docId),
    areaId: id<'AreaId'>(parsed.areaId),
    areaName: parsed.areaName,
    kind: parsed.kind,
    actorPersonId: nullableId<'PersonId'>(parsed.actorPersonId),
    actorName: parsed.actorName,
    action: parsed.action,
    detail: parsed.detail,
    documentPath: parsed.documentPath,
    targetPath: parsed.targetPath,
    createdAt: parsed.createdAt,
    updatedAt: parsed.updatedAt,
    schemaVersion: parsed.schemaVersion,
  };
};

/** Re-exported so repositories can build typed ids when composing queries. */
export type { AreaId, PersonId, SubAreaId };
